import React, { useState } from 'react';
import { apiRequest } from '../../utils/apiHelper';

import { useDataTable } from '../hooks/useDataTable';
import DataTable from '../components/shared/DataTable';
import PageHeader from '../components/shared/PageHeader';
import StatusBadge from '../components/shared/StatusBadge';
import FilterBar from '../components/shared/FilterBar';
import ConfirmModal from '../components/shared/ConfirmModal';
import DiscountCodeModal from './DiscountCodeModal';
import { useModal } from '../hooks/useModal';
import { formatPrice } from '../../utils/formatHelper';
import LoadingSpinner from '../../components/LoadingSpinner';
import { showServerError, showSuccess, showError } from '../../utils/toastHelper';

function DiscountCodeManagement() {
  const [selectedCode, setSelectedCode] = useState(null);
  const {
    isOpen: isCodeOpen,
    open: openCode,
    close: closeCode
  } = useModal();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [codeToDelete, setCodeToDelete] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const {
    data: discountCodes,
    loading,
    pagination,
    handlePageChange,
    handleLimitChange,
    handleSearch,
    handleFilterChange,
    filters,
    refresh
  } = useDataTable('/admin/discount-codes', {
    initialFilters: {
      search: '',
      isActive: 'all'
    }
  });

  const handleClearFilters = () => {
    handleFilterChange('search', '');
    handleFilterChange('isActive', 'all');
  };

  const handleOpenModal = (discountCode = null) => {
    setSelectedCode(discountCode);
    openCode();
  };

  const handleCloseModal = (shouldRefresh = false) => {
    closeCode();
    setSelectedCode(null);
    if (shouldRefresh) {
      refresh();
    }
  };

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      const response = await apiRequest(`/admin/discount-codes/${id}/toggle-status`, {
        method: 'PATCH'
      });

      if (response.success) {
        showSuccess(response.message);
        refresh();
      } else {
        throw new Error(response.message);
      }
    } catch (error) {
      if (error.apiError) {
        showServerError();
      } else {
        showError(error.message || 'Durum değiştirilirken bir hata oluştu');
      }
    }
  };

  const handleDeleteCode = (id) => {
    setCodeToDelete(id);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!codeToDelete) return;

    try {
      setActionLoading(true);
      const response = await apiRequest(`/admin/discount-codes/${codeToDelete}`, {
        method: 'DELETE'
      });

      if (response.success) {
        showSuccess(response.message);
        setShowDeleteConfirm(false);
        setCodeToDelete(null);
        refresh();
      } else {
        throw new Error(response.message);
      }
    } catch (error) {
      if (error.apiError) {
        showServerError();
      } else {
        showError(error.message || 'İndirim kodu silinirken bir hata oluştu');
      }
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Süresiz';

    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Columns Configuration
  const columns = [
    {
      header: 'Kod',
      accessor: 'code',
      render: (row) => <span className="fw-bold">{row.code}</span>
    },
    {
      header: 'Tür',
      accessor: 'type',
      render: (row) => row.type === 'PERCENTAGE' ? 'Yüzde' : 'Sabit'
    },
    {
      header: 'Değer',
      accessor: 'value',
      render: (row) => (
        row.type === 'PERCENTAGE' ?
          `%${row.value}` :
          formatPrice(row.value)
      )
    },
    {
      header: 'Min. Tutar',
      accessor: 'minPurchaseAmount',
      render: (row) => row.minPurchaseAmount ? formatPrice(row.minPurchaseAmount) : '-'
    },
    {
      header: 'Max. İndirim',
      accessor: 'maxDiscountAmount',
      render: (row) => row.maxDiscountAmount ? formatPrice(row.maxDiscountAmount) : '-'
    },
    {
      header: 'Başlangıç',
      accessor: 'startDate',
      render: (row) => formatDate(row.startDate)
    },
    {
      header: 'Bitiş',
      accessor: 'endDate',
      render: (row) => formatDate(row.endDate)
    },
    {
      header: 'Limit',
      accessor: 'usageLimit',
      render: (row) => row.usageLimit || 'Limitsiz'
    },
    {
      header: 'Kullanım',
      accessor: 'currentUsage',
      render: (row) => row.currentUsage
    },
    {
      header: 'Durum',
      accessor: 'isActive',
      render: (row) => (
        <StatusBadge status={row.isActive ? 'ACTIVE' : 'INACTIVE'} />
      )
    },
    {
      header: 'İşlemler',
      accessor: 'actions',
      render: (row) => (
        <div className="dropdown" onClick={(e) => e.stopPropagation()}>
          <button className="btn btn-sm btn-light text-muted" data-bs-toggle="dropdown">
            <i className="fas fa-ellipsis-v"></i>
          </button>
          <ul className="dropdown-menu dropdown-menu-end border-0 shadow">
            <li>
              <button className="dropdown-item" onClick={() => handleOpenModal(row)}>
                <i className="fas fa-edit me-2"></i> Düzenle
              </button>
            </li>
            <li>
              <button className="dropdown-item" onClick={() => handleToggleStatus(row.id, row.isActive)}>
                <i className={`fas fa-${row.isActive ? 'ban' : 'check'} me-2`}></i>
                {row.isActive ? 'Pasif Yap' : 'Aktif Yap'}
              </button>
            </li>
            {row.currentUsage === 0 && (
              <li>
                <button className="dropdown-item text-danger" onClick={() => handleDeleteCode(row.id)}>
                  <i className="fas fa-trash me-2"></i> Sil
                </button>
              </li>
            )}
          </ul>
        </div>
      )
    }
  ];

  // Show full page loader on initial load
  if (loading && (!discountCodes || discountCodes.length === 0)) {
    return (
      <div className="container-fluid p-0">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <LoadingSpinner message="İndirim kodları yükleniyor..." size="medium" />
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid p-0">
      <PageHeader
        title="İndirim Kodları"
        description="İndirim kodlarını yönetin"
        breadcrumbs={[{ label: 'Dashboard', path: '/admin' }, { label: 'İndirim Kodları' }]}
        actions={
          <button className="btn btn-primary btn-sm-mobile" onClick={() => handleOpenModal()}>
            <i className="fas fa-plus me-2"></i> Yeni İndirim Kodu
          </button>
        }
      />

      {/* Filters */}
      <FilterBar
        filters={[
          {
            type: 'select',
            key: 'isActive',
            label: 'Durum',
            defaultValue: 'all',
            options: [
              { value: 'all', label: 'Tüm Durumlar' },
              { value: 'true', label: 'Aktif' },
              { value: 'false', label: 'Pasif' }
            ]
          }
        ]}
        activeFilters={filters}
        onFilterChange={(key, value) => {
          handleFilterChange(key, value);
        }}
        onClearFilters={handleClearFilters}
        searchPlaceholder="İndirim kodu ara..."
      />

      {/* Content */}
      <DataTable
        tableId="discount_codes"
        columns={columns}
        data={discountCodes}
        loading={loading}
        pagination={pagination}
        onPageChange={handlePageChange}
        onLimitChange={handleLimitChange}
        onRowClick={handleOpenModal}
        emptyState={{
          title: 'İndirim Kodu Bulunamadı',
          message: 'Gösterilecek indirim kodu bulunmuyor.',
          icon: 'tags'
        }}
      />

      {/* Discount Code Modal */}
      <DiscountCodeModal
        isOpen={isCodeOpen}
        onClose={handleCloseModal}
        discountCode={selectedCode}
      />

      <ConfirmModal
        show={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setCodeToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="İndirim Kodunu Sil"
        message="Bu indirim kodunu silmek istediğinize emin misiniz? Bu işlem geri alınamaz."
        confirmText="Sil"
        cancelText="İptal"
        variant="danger"
        loading={actionLoading}
      />
    </div>
  );
}

export default DiscountCodeManagement; 